import deepEqual from "deep-equal"
import memoize from "memoize-one"
import { v4 as uuid } from "uuid"
import { lch } from "d3-color"
import { schemeCategory10 } from "d3-scale-chromatic"
import { scaleOrdinal, scaleLinear } from "d3-scale"
import { extent } from "d3-array"
import { selectAll } from "d3-selection"
import { max, min } from "d3-array"

import { arrayWithContents, parseDate, sortDates, isNumber } from "../../utils"
import { id_accessor, REACTOR_COLOR_ASSIGNMENTS } from "../../../components/shared_components_registration"
import { makePlotFactoryForConfig } from "../../../components/graphing/plot_factories/PlotRegistry"
import TIME_EXTRACTORS from "./TimeExtractors"

export const cleanSmpSrcLabel = (obs) => (obs?.smp_src || "").split("__").join(" ")
export const getTagKey = (obv) => `${obv.tag}_${obv.smp_src}`
export const getUniqueTags = (graph, tag_category) => [
  ...new Set(graph[tag_category].map((o) => getTagKey(o))),
]

// Combination of tag, smp_src and [id_accessor] should be unique and a good way to identify a trend for editing
export const getMatchingTrend = (obs, refObs, ignoreRunID = false) => {
  if (ignoreRunID) {
    return obs.tag === refObs.tag && obs.smp_src === refObs.smp_src
  } else {
    return (
      obs.tag === refObs.tag && obs.smp_src === refObs.smp_src && obs[id_accessor] === refObs[id_accessor]
    )
  }
}

export const getMatchingTrendFromList = (obs, refObsList = [], ignoreRunID = false) => {
  let match
  refObsList.forEach((refObs) => {
    const isMatch = getMatchingTrend(obs, refObs, ignoreRunID)
    if (isMatch) {
      match = refObs
    }
  })

  return match
}

// isSupplementalCalcPlot:
// Currently limited to certain plots (e.g. Eth_Tons and Daily_Avg_QG_NM3) which are calculated in a way external to the
// main calculation mechanism. No sense in enabling the modal to show the calc given this consideration.
export const homeDashSpecialStr = "HomeDash Special"
export const isHomeDashPlot = (graph) => graph.category === homeDashSpecialStr
export const isSupplementalCalcPlot = (graph) =>
  graph.special === "site-wide" && (isHomeDashPlot(graph) || graph.design_reactors || graph.allowed_reactors)

export const COLORS_SCHEMECAT_10 = scaleOrdinal(schemeCategory10)

export const non_observation_graphs = ["mutations", "frequencies", "heatmap"]

const getAxisOneUnits = (obs, active_only = false) => [
  ...new Set(obs.filter((o) => (active_only ? !o.disabled && !o.axis2 : !o.axis2)).map((o) => o.units)),
]
const getAxisTwoUnits = (obs, active_only) => [
  ...new Set(obs.filter((o) => (active_only ? !o.disabled && o.axis2 : o.axis2)).map((o) => o.units)),
]

export function cleanObservationAxisAssignment(observations, graph, tag_category) {
  /*
    Carry out any needed tag-axis reassignments and axes relabelling
  */
  let axis1units = getAxisOneUnits(observations, true)
  let axis2units = getAxisTwoUnits(observations, true)
  const active_obs = observations.filter((o) => !o.disabled)

  // Unify any split axes units assignments
  const shared_units = axis1units.filter((value) => axis2units.includes(value))
  if (axis1units.length > 1) {
    shared_units.forEach((unit) => axis1units.splice(axis1units.indexOf(unit), 1))
  } else {
    shared_units.forEach((unit) => axis2units.splice(axis2units.indexOf(unit), 1))
  }

  if (axis1units.length === 0) {
    observations.forEach((obs) => delete obs.axis2)
    axis1units = axis2units
    axis2units = []
  }

  if ([...axis1units, ...axis2units].length === 1 && active_obs.length === 2) {
    let count = 0
    observations.forEach((obs) => {
      if (!obs.disabled) {
        if (count === 0) {
          delete obs.axis2
          count += 1
        } else {
          obs.axis2 = true
          axis2units.push(obs.units)
        }
      }
    })
  } else {
    observations.forEach((obs) => {
      if (axis1units.includes(obs.units)) {
        delete obs.axis2
      } else if (axis2units.includes(obs.units)) {
        obs.axis2 = true
      }
    })
  }

  graph[tag_category] = observations
  graph = generalPlotSettingsCleanup(graph, tag_category)

  // Update Axis Labels
  if ([...new Set([...axis1units, ...axis2units])].length === 1 && active_obs.length === 2) {
    graph.axis1label = `${active_obs[0].tag} (${active_obs[0].units})`
    graph.axis2label = `${active_obs[1].tag} (${active_obs[1].units})`
  } else {
    graph.axis1label = axis1units.join(" / ")
    graph.axis2label = axis2units.join(" / ")
  }
  // -- Update Axis Labels

  return graph
}

export const generalPlotSettingsCleanup = (graph, tag_category) => {
  // General cleanup for graph settings

  // Figure out what units are on which axes
  let axis1units = getAxisOneUnits(graph[tag_category])
  let axis2units = getAxisTwoUnits(graph[tag_category])

  // Clean-up 'Presets' category to remove association with preset graphs
  graph.category === "Presets" ? delete graph.category : null

  // Clean-up plot metrics that are tag level, but ended up in the main plot setting
  const miscKeys = ["dcs_logging_info", "keywords", "matchExplanation", "disabled"]
  miscKeys.forEach((key) => {
    graph[key] ? delete graph[key] : null
  })

  // Clean Empty Axes
  if (axis1units.length === 0) {
    delete graph.outlierFilter
    delete graph.axis1domain
    delete graph.axis1label
  }

  if (axis2units.length === 0) {
    delete graph.outlierFilter2
    delete graph.axis2domain
    delete graph.axis2label
  }
  // -- Clean Empty Axes

  return graph
}

export function newPlotTrendsUpdate(op, tags = [], tag_category = "observations") {
  /*
     Following user selection of tag(s) to add to graph, we need to figure out which axis to assign tag(s) to.
     Will look up the units currently represented on each axis and assign new tags to an axis based on matching units
     (if applicable). Also provides new trends with an assigned color.

     op: Object containing graph settings
     tags: List of tag objects to (potentially) add to graph
     tag_category: "observations" or "tags" - key in op where trends can be found
  */
  let plot = Object.assign({}, op)
  let observations = Object.assign([], plot[tag_category] || [])
  let plot_updated = false
  tags.forEach((sel) => {
    const tagInfo = sel.observations[0]
    const matching_tag = observations.find((obs) => getMatchingTrend(obs, tagInfo))
    // Tag not already in observations
    if (!matching_tag) {
      plot_updated = true
      const tag_units = tagInfo.units
      const axis2 = assignTagToAxis2(observations, tag_units)
      observations.push({
        ...tagInfo,
        axis2: axis2,
        colour: COLORS_SCHEMECAT_10(observations.length - 1),
      })
    }
  })

  return [observations, plot, plot_updated]
}

export const assignTagToAxis2 = (observations, units) => {
  // Returns true or false. True means assign tag to axis 2.
  const axis1units = getAxisOneUnits(observations, true)
  const axis2units = getAxisTwoUnits(observations, true)

  return (
    axis2units.includes(units) ||
    (!axis1units.includes(units) &&
      [...new Set([...axis1units, ...axis2units])].length === 1 &&
      !(axis1units.length === 0))
  )
}

export function groupBy(values, keyfn) {
  const map = {}

  for (const value of values) {
    const key = keyfn(value)
    let l = map[key]
    if (!l) {
      l = map[key] = []
    }
    l.push(value)
  }

  return map
}

export function handleFloatingPointImprecision(value) {
  return parseFloat(value.toFixed(10))
}

export function getRoundedMinMax(minY, maxY, height) {
  // We want scaling of axis to be built upon more round min/max values
  // Getting an idea of scale magnitude here to round minY/maxY if necessary
  // Only doing for computed min/max and NOT set domains or zooming
  let axis = scaleLinear().domain([minY, maxY]).range([height, 0])
  let axis_ticks = axis.ticks()
  let tick_interval = handleFloatingPointImprecision(axis_ticks[1] - axis_ticks[0])
  minY = minY !== axis_ticks[0] ? handleFloatingPointImprecision(axis_ticks[0] - tick_interval) : minY
  maxY =
    maxY !== axis_ticks[axis_ticks.length - 1]
      ? handleFloatingPointImprecision(axis_ticks[axis_ticks.length - 1] + tick_interval)
      : maxY

  return [minY, maxY]
}

function getQ1Q3(q1Range, q3Range) {
  const even = q1Range.length % 2 === 0
  let q1, q3

  if (even) {
    q1 = (q1Range[q1Range.length / 2] + q1Range[q1Range.length / 2 - 1]) / 2
    q3 = (q3Range[q3Range.length / 2] + q3Range[q3Range.length / 2 - 1]) / 2
  } else {
    q1 = q1Range[Math.floor(q1Range.length / 2)]
    q3 = q3Range[Math.floor(q3Range.length / 2)]
  }

  return [q1, q3]
}

export function getRangeWithoutOutliers(layer, accessor) {
  let q1_q3, iqr, lowerLimit, upperLimit
  let allObservations = layer && layer.data ? layer.data.map((obs) => accessor(obs)) : []
  allObservations = allObservations.sort((a, b) => {
    return a - b
  })
  let even = allObservations.length % 2 === 0

  if (allObservations.length === 1) {
    return [allObservations[0], allObservations[0]]
  }

  if (even) {
    let q1Range = allObservations.slice(0, allObservations.length / 2)
    let q3Range = allObservations.slice(allObservations.length / 2, allObservations.length)
    q1_q3 = getQ1Q3(q1Range, q3Range)
  } else {
    let q1Range = allObservations.slice(0, Math.floor(allObservations.length / 2))
    let q3Range = allObservations.slice(Math.ceil(allObservations.length / 2), allObservations.length)
    q1_q3 = getQ1Q3(q1Range, q3Range)
  }

  const q1 = q1_q3[0]
  const q3 = q1_q3[1]
  iqr = q3 - q1
  lowerLimit = q1 - 1.5 * iqr
  upperLimit = q3 + 1.5 * iqr
  const outliersRemoved = allObservations.filter((obs) => obs <= upperLimit && obs >= lowerLimit)

  return extent(outliersRemoved)
}

export function filterOnlineOfflineData(data, graphKey) {
  /*
   * Related to Sample based trends:
   *      A. SampleObservations (formerly ExternalObservations)
   *      B. Observations with sample_id fk
   */
  if (arrayWithContents(data) && graphKey.hideOnline) {
    return data.filter((d) => d["is_sample"])
  } else if (arrayWithContents(data) && graphKey.hideOffline) {
    return data.filter((d) => !d["is_sample"])
  } else {
    return data
  }
}

export function filterDataOnSampleType(data, graphKey) {
  /*
   * Related to Sample based trends:
   *      A. SampleObservations (formerly ExternalObservations)
   *      B. Observations with sample_id fk
   *
   * 1. Allow users to specify which SampleType/SampleSource(s) they want to view/export data related to.
   * 2. If user chooses to view averaged data, take the average value for any data for a given trend if sharing a
   * timestamp and SampleType
   *
   * */
  if (arrayWithContents(data) && arrayWithContents(data.filter((d) => d.is_sample))) {
    if (graphKey.sampleTypes) {
      data = data.filter((d) => !d.is_sample || (d.is_sample && graphKey.sampleTypes.includes(d.sample_type)))
    }

    if (graphKey.avgSampleData) {
      // Applying averaging to sample based data ONLY
      const averageableData = data.filter((d) => d.is_sample)
      const unique_days = [...new Set(averageableData.map((d) => d.day))]
      const unique_dayType_obs = []
      unique_days.forEach((d) => {
        const day_match_obs = averageableData.filter((o) => o.day === d)
        const unique_sample_types = [...new Set(day_match_obs.map((o) => o.sample_type))]
        unique_sample_types.forEach((st) => {
          const match_obs = day_match_obs.filter((o) => o.sample_type === st)
          const value_arr = match_obs.map((o) => o.value)
          const avg_value = value_arr.reduce((a, b) => a + b, 0) / value_arr.length
          const unique_notes = [...new Set(match_obs.filter((o) => o.note).map((o) => o.note))]
          let supp = {}
          if (avg_value === 0 && unique_notes.length > 0) {
            supp["note"] = unique_notes.join(", ")
          }

          unique_dayType_obs.push({
            ...supp,
            ...match_obs[0],
            value: avg_value,
            sample_id: match_obs.map((o) => o.sample_id).join(", "),
          })
        })
      })
      data = sortDates([...unique_dayType_obs, ...data.filter((d) => !d.is_sample)], "timestamp", true)
    }
  }

  return data
}

export const adjustTrendColor = (trendColor, lChange, cChange) => {
  // Allowed ranges -- l: 0-100, c: 0-230 (roughly), h: 0-360
  const { l, c, h } = lch(trendColor)
  let lVal = l + lChange
  if (lVal < 0) {
    lVal = 0
  } else if (lVal > 100) {
    lVal = 100
  }
  let cVal = c + cChange
  if (cVal < 0) {
    cVal = 0
  } else if (cVal > 230) {
    cVal = 230
  }
  return lch(lVal, cVal, h).formatHex()
}

export function toggleSingleMultiRunPlot(
  index,
  op,
  plotConfigUpdater,
  multi,
  useReactorColors = false,
  onlyReactorNames = false,
) {
  let plot_updated = Object.assign({}, op)

  if (plot_updated) {
    if (multi) {
      // Plot state is switching from multi-run to single-run
      plot_updated["observations"] = plot_updated.tags
      delete plot_updated.special
      delete plot_updated.tags
    } else {
      // Plot state is switching from single-run to multi-run or multi-run plot is updating runs on present on plot
      if ((plot_updated["tags"] || []).length === 0) {
        // If initially toggling from single-run to multi-run
        plot_updated["tags"] = plot_updated.observations
      } else if ((plot_updated["observations"] || []).length > 0) {
        // Check if new tag added to plot
        const newObs = op.observations
          .map((t) => {
            const { [id_accessor]: id, ...obs } = t
            return obs
          })
          .filter((obs) => !getMatchingTrendFromList(obs, op.tags || []))

        plot_updated["tags"] = [...plot_updated["tags"], ...newObs].filter(
          (obs) => getMatchingTrendFromList(obs, op.observations || [], true), // Filter for tags removed from plot
        )
      }

      plot_updated["observations"] = []
      plot_updated["special"] = "multi"

      const { runs = [], runData = {}, colors = COLORS_SCHEMECAT_10 } = this.props
      const { tags = [] } = plot_updated
      const runsRestricted = Object.keys(plot_updated).includes("restrictedRuns") // On rare occasion on multiRun plots we don't want to plot all runs on the graph
      const plotRuns = !runsRestricted ? runs : plot_updated.restrictedRuns

      const tagColorsBase = {} // Establish a baseline of a color for all tags to be overlaid
      plotRuns.forEach((run_id, i) => {
        const data = runData[run_id]
        if (data && !data?.loading && !data?.err) {
          tags.forEach((refObs) => {
            const tagKey = getTagKey(refObs)
            if (!Object.keys(tagColorsBase).includes(tagKey)) {
              // Establish a baseline of a color for a given tag
              tagColorsBase[tagKey] = refObs.colour || colors(Object.keys(tagColorsBase).length)
            }
            // Playing with the idea of minor color tweaks for different runs to distinguish them
            // Current logic just toggles btw two color palettes: A) reference lch and B) l+20, c-10, h
            const isEvenRun = (i + 1) % 2 === 0
            const lChange = isEvenRun ? 20 : 0
            const cChange = isEvenRun ? -10 : 0
            const tagRunColor = adjustTrendColor(tagColorsBase[tagKey], lChange, cChange)

            // Grab current run/tag settings to maintain any user color/style/label/etc. changes
            const currObs =
              getMatchingTrendFromList({ ...refObs, [id_accessor]: run_id }, op.observations || []) || {}

            const getMetric = (metric, defaultVal) => currObs[metric] || refObs[metric] || defaultVal

            plot_updated.observations.push({
              tag: refObs.tag,
              smp_src: refObs.smp_src,
              units: refObs.units,
              [id_accessor]: run_id,
              axis2: currObs.tag ? currObs.axis2 : refObs.axis2, // Handle boolean field
              disabled: currObs.tag ? currObs.disabled : refObs.disabled, // Handle boolean field
              colour:
                currObs.colour ||
                (REACTOR_COLOR_ASSIGNMENTS?.[data?.reactor] && useReactorColors && !runsRestricted
                  ? REACTOR_COLOR_ASSIGNMENTS[data.reactor]
                  : tagRunColor),
              style: currObs.style || (runsRestricted ? refObs.style : "linespoints"),
              pointSize: getMetric("pointSize"),
              pointModifier: getMetric(
                "pointModifier",
                isEvenRun && !useReactorColors ? "triangle" : "points",
              ),
              strokeWidth: getMetric("strokeWidth"),
              strokeModifier: getMetric("strokeModifier"),
              label:
                currObs.label ||
                `${refObs.label || refObs.tag} [${
                  onlyReactorNames ? data.reactor : `${data.reactor}-${data.name}`
                }]`,
            })
          })
        }
      })
    }

    plot_updated = cleanObservationAxisAssignment(plot_updated["observations"], plot_updated, "observations")

    // Only update plot if anything changed
    !graphsEqual(op, plot_updated) && plotConfigUpdater(plot_updated, index)
  }
}

export function zoomGraphsEqual(g1, g2) {
  if (!g1 && !g2) {
    return true
  } else if ((!g1 && g2) || (g1 && !g2)) {
    return false
  } else if (g1 && g2) {
    return (
      g1.special === g2.special &&
      g1.title === g2.title &&
      g1.gridlines === g2.gridlines &&
      g1.outlierFilter === g2.outlierFilter &&
      g1.outlierFilter2 === g2.outlierFilter2 &&
      deepEqual(g1.observations, g2.observations) &&
      deepEqual(g1.tags, g2.tags)
    )
  }
}

export function graphsEqual(g1, g2) {
  if (!g1 && !g2) {
    return true
  } else if ((!g1 && g2) || (g1 && !g2)) {
    return false
  } else if (g1 && g2) {
    return (
      g1.special === g2.special &&
      g1.axis1label === g2.axis1label &&
      g1.axis2label === g2.axis2label &&
      g1.category === g2.category &&
      g1.title === g2.title &&
      g1.gridlines === g2.gridlines &&
      g1.outlierFilter === g2.outlierFilter &&
      g1.outlierFilter2 === g2.outlierFilter2 &&
      g1.hideOnline === g2.hideOnline &&
      g1.hideOffline === g2.hideOffline &&
      g1.sampleTypes === g2.sampleTypes &&
      g1.avgSampleData === g2.avgSampleData &&
      deepEqual(g1.xaxisdomain, g2.xaxisdomain) &&
      deepEqual(g1.axis1domain, g2.axis1domain) &&
      deepEqual(g1.axis2domain, g2.axis2domain) &&
      deepEqual(g1.observations, g2.observations) &&
      deepEqual(g1.tags, g2.tags) &&
      deepEqual(g1.microbes, g2.microbes)
    )
  }
}

export const makeXAxis = (timeExtractor, minTime, maxTime, minPos, maxPos) =>
  timeExtractor.buildAxis(minTime, maxTime, minPos, maxPos)

/*
 * Placeholder for things that we want to cache per-run.
 *
 * Batons are cached based on opaque
 * keys (uuids), which are managed by the enclosing MultiRunView, and MUST NOT
 * be reused.
 */
export class RunBaton {
  makeAxis = memoize(makeXAxis, deepEqual)

  timecourseForRun = memoize((run, viewerState, plotFactories, timeExtractor) => {
    const timeRanges = [],
      summaryRanges = []
    for (const { plotKey } of plotFactories) {
      const plotBaton = this.plotBatonsByKey[plotKey]
      if (!plotBaton) continue
      const timeRange = plotBaton.getTimeAxes(run, viewerState, timeExtractor)
      if (timeRange && (timeRange[0] || timeRange[1])) {
        if (plotBaton?.config && isHomeDashPlot(plotBaton.config)) {
          summaryRanges.push(timeRange[0])
          summaryRanges.push(timeRange[1])
        } else {
          timeRanges.push(timeRange[0])
          timeRanges.push(timeRange[1])
        }
      }
    }

    return extent(timeRanges.length > 0 ? timeRanges : summaryRanges)
  })

  plotBatonsByKey = {}
  plotPropsByKey = {}
  axesByKey = {}
}

export function showGraphModal(modal) {
  this.setState({
    [`${modal}`]: true,
  })
}

export function hideGraphModal(modal) {
  this.setState({
    [`${modal}`]: false,
  })
}

export function getAvailableRuns(runs, runData) {
  const availableRuns = []
  runs.forEach((runID, i) => {
    const run = runData[runID]
    if (!run) {
      // Skip
    } else if (run.loading) {
      availableRuns.push({
        [id_accessor]: runID,
        observationTypes: {},
        loading: true,
      })
    } else if (run.err) {
      availableRuns.push({
        [id_accessor]: runID,
        observationTypes: {},
        err: run.err,
        forceReload: run.forceReload,
      })
    } else {
      run["color"] = REACTOR_COLOR_ASSIGNMENTS[run.reactor] || COLORS_SCHEMECAT_10(i)
      availableRuns.push(run)
    }
  })
  return availableRuns
}

export function ensureDataRequested(runs, runData, plotEntries, force_reload = false) {
  runs.forEach((runID, idx) => {
    const run = runData[runID]
    !(!run || run.err || run.loading) &&
      plotEntries.forEach((plotEntry) => {
        const { plotFactory } = plotEntry
        plotFactory && plotFactory.ensureDataRequested(run, force_reload, idx === 0)
      })
  })
}

export function getPlotFactories(plots, updateGraphs, cache = [], single = false) {
  const result = []

  PLOT_LOOP: for (const plot of plots) {
    /* If graph and cached graph are equal there's no need to regenerate factory */
    for (const cacheEntry of cache) {
      if (graphsEqual(plot, cacheEntry.plot)) {
        result.push(cacheEntry)
        continue PLOT_LOOP
      }
    }

    /* If graph and cached graph are not equal or there is no cached graph generate factory */
    const factory = makePlotFactoryForConfig(plot)
    const graphSetting = {
      plot: plot,
      plotFactory: factory,
      plotConfigUpdater: (newPlotConfig, idx) => {
        // pass updateGraphs a function that updates the graphs in the store
        updateGraphs((graphs) => {
          const plots = Object.assign([], graphs)
          return plots.map((oldPlotConfig, i) => {
            if (graphsEqual(oldPlotConfig, plot) && (!idx || idx === i)) {
              return newPlotConfig
            } else {
              return oldPlotConfig
            }
          })
        })
      },
    }

    if (single) {
      graphSetting["plotBaton"] = factory && factory.makePlotBaton()
    } else {
      graphSetting["plotKey"] = uuid()
    }

    result.push(graphSetting)
  }

  return result
}

export function makeObservationList(...observationTypeSets) {
  let categories = {}
  let seenBefore = []

  for (const observationTypes of observationTypeSets) {
    Object.keys(observationTypes).map((key) => {
      observationTypes[key] &&
        observationTypes[key].forEach((d) => {
          let comboName = `${key}__${d.tag_name || d.tag}` //TODO - resolve tag_name/tag LL/LTPM keys
          if (!seenBefore.includes(comboName)) {
            // Need to handle situation where tag has >1 key
            seenBefore.push(comboName)
            categories[d.tag_name || d.tag] = {
              tag_name: d.tag_name || d.tag,
              tag_label: d.tag_label || d.label,
              units: d.units,
              unit_alternatives: d.unit_alternatives || [],
              type: key,
            }
          }
        })
    })
  }
  return categories
}

/* Returns a single value representing the max time span across all runs and plots */
export function getMaxTimeSpan(timeScales, availableRuns, timeExtractor, alignment, liveDash = false) {
  const minTimeSpan = extent(timeScales.map((ts) => ts[0]))[0] // on liveDash min display time is not based on ino date, rather the min of data loaded in
  const minInoculationDate = Math.min(...availableRuns.map((run) => run.inoculated_on))
  return timeScales
    .map(([minTime, maxTime], i) => {
      if (maxTime) {
        const run = availableRuns[i]
        const displayMin = !(timeExtractor.name === "Timestamp" && alignment.name === "Timestamp")
          ? timeExtractor.getOriginTime(run.inoculated_on, minTime, liveDash)
          : liveDash
            ? minTimeSpan
            : parseDate(minInoculationDate)
        return timeExtractor.difference(maxTime, displayMin)
      } else {
        return 0
      }
    })
    .reduce((span1, span2) => Math.max(span1, span2), 0)
}

/* Grabs any variables that may apply across all plots on a dashboard */
export function getCrossPlotProps(plotFactories, availableRuns, runBatons) {
  return plotFactories
    .filter((plotEntry) => plotEntry?.plot && plotEntry?.plotFactory)
    .map((plotEntry) =>
      plotEntry.plotFactory.getCrossPlotProps(
        availableRuns
          .filter((run) => runBatons[run[id_accessor]]?.plotPropsByKey[plotEntry.plotKey])
          .map((run) => runBatons[run[id_accessor]]?.plotPropsByKey[plotEntry.plotKey]),
      ),
    )
}

/* Returns a list. Get [min, max] per run across all plot observations and operator action times */
export function getRunTimeScales(plotFactories, availableRuns, timeExtractor, viewerState, runBatons = {}) {
  return availableRuns.map((run) => {
    const baton = runBatons[run[id_accessor]]
    if (baton) {
      return baton.timecourseForRun(run, viewerState, plotFactories, timeExtractor)
    } else {
      return []
    }
  })
}

/* Returns a list. Each entry is an object containing time, y, and y2 (when applicable) keys with
 * [min, max] values representing the spread of values for each axis across all runs */
export function getUnifiedPlotAxes(plotFactories, availableRuns, runBatons, getDisplayMin) {
  return plotFactories
    .filter((plotEntry) => plotEntry?.plot)
    .map((plotEntry) => {
      const axesAndRanges = {}
      for (const run of availableRuns) {
        const runAxes = runBatons[run[id_accessor]]?.axesByKey[plotEntry.plotKey] || {}
        for (const axis of Object.keys(runAxes || {})) {
          const runRange = runAxes[axis]
          if (axesAndRanges[axis]) {
            if (runRange && runRange[0] !== undefined && runRange[1] !== undefined) {
              axesAndRanges[axis] = [
                Math.min(
                  (getDisplayMin ? getDisplayMin(availableRuns, plotEntry, axis) : null) ||
                    axesAndRanges[axis][0],
                  runRange[0],
                ),
                Math.max(axesAndRanges[axis][1], runRange[1]),
              ]
            }
          } else {
            axesAndRanges[axis] = runRange
          }
        }
      }
      return axesAndRanges
    })
}

/* Further sets up baton for each run in runBatons with variables mapped out per plot uuid key:
 * (axesByKey, plotBatonsByKey, plotPropsByKey) */
export function getRunBatonVarsPerPlotKey(
  plotFactories,
  availableRuns,
  timeExtractor,
  viewerState,
  runBatons,
  filterPlotEntry, // Any extra filtering logic need to apply?
) {
  const updatedBatons = {}
  for (const run of availableRuns) {
    const run_id = run[id_accessor]
    let baton = runBatons[run_id] || new RunBaton()
    for (const plotEntry of plotFactories) {
      if (!plotEntry.plot || !plotEntry.plotFactory || (filterPlotEntry && filterPlotEntry(plotEntry, run))) {
        continue
      }
      let plotBaton = baton.plotBatonsByKey[plotEntry.plotKey]
      if (!plotBaton) {
        plotBaton = baton.plotBatonsByKey[plotEntry.plotKey] = plotEntry.plotFactory.makePlotBaton()
      }
      baton.plotPropsByKey[plotEntry.plotKey] = plotBaton.getPlotProps(run, viewerState)
      baton.axesByKey[plotEntry.plotKey] = plotBaton.getAxesAndRanges(run, viewerState, timeExtractor)
    }
    updatedBatons[run_id] = baton
  }
  return updatedBatons
}

export function configureXAxisPerRun(
  availableRuns,
  timeScalesByRun,
  timeExtractor,
  maxTimeSpan,
  chartWidth,
  suppChartWidth = 0, // Scaling for any non-main plots on page [if applicable]
  perRunTimeOffsets = {},
  propsMinTime,
  propsMaxTime,
  alignment,
  getRunBaton,
  useAltOrigin = false,
) {
  const xAxisParameters = {},
    xScales = {},
    suppXScales = {}
  this.timeWarning = availableRuns.length > 0 // Will reset if any run has data in-range
  const crossRunMinTime = extent(timeScalesByRun.map((ts) => ts[0]))[0]
  const isTimestampAligned = timeExtractor.name === "Timestamp" && alignment.name === "Timestamp"

  for (let i = 0; i < availableRuns.length; ++i) {
    const [minTime, maxTime] = timeScalesByRun[i],
      run = availableRuns[i],
      run_id = run[id_accessor],
      offset = perRunTimeOffsets?.[run_id] || 0,
      displayMin = isTimestampAligned
        ? crossRunMinTime
        : timeExtractor.getOriginTime(run.inoculated_on, minTime, useAltOrigin),
      chartStart = 0

    this.baseTimesByRun[run_id] = timeExtractor.offsetDays(displayMin, offset)

    const baton = getRunBaton(run_id)
    if (baton) {
      const x = baton.makeAxis(
        timeExtractor,
        timeExtractor.offsetDays(displayMin, (propsMinTime || 0) + offset),
        timeExtractor.offsetDays(
          displayMin,
          (propsMaxTime || isNumber(propsMaxTime) ? propsMaxTime : maxTimeSpan) + offset,
        ),
        chartStart,
        chartWidth,
      )
      xScales[run_id] = x

      if (suppChartWidth) {
        suppXScales[run_id] = baton.makeAxis(
          timeExtractor,
          timeExtractor.offsetDays(displayMin, propsMinTime || 0),
          timeExtractor.offsetDays(
            displayMin,
            propsMaxTime || isNumber(propsMaxTime) ? propsMaxTime : maxTimeSpan,
          ),
          chartStart,
          suppChartWidth,
        )
      }

      xAxisParameters[run_id] = {
        displayMin: displayMin,
        minTime: propsMinTime,
        maxTime: propsMaxTime,
        maxTimeSpan: maxTimeSpan,
        chartWidth: chartWidth,
      }

      if (
        maxTime !== undefined &&
        minTime !== undefined &&
        timeExtractor.difference(x.domain()[0], maxTime) <= 0 &&
        timeExtractor.difference(x.domain()[1], minTime) >= 0
      ) {
        this.timeWarning = false
      }
    }
  }

  return [xScales, suppXScales, xAxisParameters]
}

export function getPlotHeight(height, margin, timeExtractor) {
  return height + margin.top + margin.bottom + (timeExtractor.name !== "Day" ? 50 : 0)
}

export const getAfterDragElementOrder = (klass, element, x, y, updateListOrder) => {
  const allDragItems = [...selectAll(`.${klass}`)._groups[0]]
  if (allDragItems.length === 0) return
  const distance_object = allDragItems.map((child) => {
    const box = child.getBoundingClientRect()
    return {
      vertical: y - box.top - box.height / 2,
      horizontal: x - box.left - box.width / 2,
      index: child.getAttribute("index"),
    }
  })

  // Vertical positioning - Figuring out which row the dragged element is closest to
  const rowYValue = min(distance_object.map((obj) => Math.abs(obj.vertical)))
  const itemsInMatchingRow = distance_object.filter(
    (obj) => obj.vertical === rowYValue || -1 * obj.vertical === rowYValue,
  )

  // Horizontal positioning - Figuring out which column the dragged element is closest to
  let newIndex
  if (itemsInMatchingRow.every((item) => item.horizontal < 0)) {
    // Item released on the left-hand extreme of the row
    // Max because dealing with negative numbers. We want the smallest negative number.
    const closest_offset = max(itemsInMatchingRow.map((item) => item.horizontal))
    newIndex = parseInt(itemsInMatchingRow.filter((item) => item.horizontal === closest_offset)[0]["index"])
  } else if (itemsInMatchingRow.every((item) => item.horizontal > 0)) {
    // Item released on the right-hand extreme of the row
    // Min because dealing with positive numbers. We want the smallest positive number.
    const closest_offset = min(itemsInMatchingRow.map((item) => item.horizontal))
    let gene_to_shift_after = parseInt(
      itemsInMatchingRow.filter((item) => item.horizontal === closest_offset)[0]["index"],
    )
    newIndex = gene_to_shift_after + 1
  } else {
    // Item released somewhere mid-row. Need to find the transition from negative to positive offset.
    let before_item, after_item
    itemsInMatchingRow.forEach((item, idx) => {
      if (idx === 0) {
        before_item = parseInt(item.index)
      } else {
        if (Math.sign(item.horizontal) === -1 && !after_item) {
          after_item = parseInt(item.index)
        } else if (!after_item) {
          before_item = parseInt(item.index)
        }
      }
    })
    newIndex = after_item
  }

  // Handle change of index
  updateListOrder && updateListOrder(newIndex)
}

export const getPlotTimeExtractor = (plot) =>
  TIME_EXTRACTORS.find((t) => t.name === plot?.["plotTimeExtractor"])

export const getXValue = (obs, timeExtractor, trendDayOffset = 0) => {
  if (timeExtractor.name === "Day" && trendDayOffset) {
    return timeExtractor.getObservationTime(obs) + trendDayOffset
  } else {
    return timeExtractor.getObservationTime(obs)
  }
}

export const adjustXAxisValue = (val, timeExtractor, trendDayOffset = 0) => {
  if (timeExtractor.name === "Day" && trendDayOffset) {
    return val - trendDayOffset
  } else {
    return val
  }
}

export const getSiteReactors = (runData, runs) =>
  Object.values(runData)
    .filter((r) => !r?.loading && !r?.err && runs.includes(r?.[id_accessor]))
    .map((r) => {
      return {
        reactor: r.reactor,
        site: r.site,
      }
    })
